import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { totalsByType, type BookingRow, type BookingTypeTotals } from "@/lib/booking-revenue";
import { getProduct } from "@/lib/shop-products";
import {
  DateRangePicker,
  formatRange,
  lastNDays,
  rangeDays,
  type AdminRange,
} from "@/components/admin/DateRangePicker";
import { WeekAssignmentCard } from "@/components/admin/WeekAssignmentCard";
import { BookingForecastPanel } from "@/components/admin/BookingForecastPanel";
import { PayoutAlertCard } from "@/components/admin/PayoutAlertCard";

import {
  BarChart3,
  CalendarDays,
  DollarSign,
  Receipt,
  TrendingUp,
  Image as ImageIcon,
  Mail,
  RefreshCw,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/dashboard")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "viewMoney")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Admin Dashboard — Coffman Cove Lodging" },
      {
        name: "description",
        content: "Visitor metrics, photo review queue, booking requests, and newsletter signups.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminDashboard,
});

type PageView = { path: string; device: string | null; referrer: string | null; visitor_hash: string | null; created_at: string };
type Submission = { id: string; submitter_name: string; caption: string | null; created_at: string };
type Booking = { id: string; name: string; email: string; arrival_date: string | null; party_size: number | null; status: string; created_at: string };
type SeasonRow = {
  id: string;
  status: string;
  arrival_date: string | null;
  departure_date: string | null;
  party_size: number | null;
  package: string | null;
  notes: string | null;
  created_at: string;
};
type Signup = { id: string; email: string; name: string | null; source: string; unsubscribed: boolean; created_at: string };
type OrderItem = { id?: string; name?: string; qty?: number; unit_price_cents?: number; category?: string };
type Order = {
  id: string;
  customer_name: string;
  status: string;
  subtotal_cents: number;
  shipping_cents: number;
  total_cents: number;
  items?: OrderItem[] | null;
  booking_request_id?: string | null;
  created_at: string;
};

/** Money was returned to the customer: counts as gross, subtracted from net. */
const REFUND_STATUSES = new Set(["refunded", "refund", "partially_refunded", "returned"]);
/** Bank/card reversal: counts as gross, subtracted from net. */
const CHARGEBACK_STATUSES = new Set(["chargeback", "chargedback", "charged_back", "disputed", "dispute"]);
/** Never captured any money at all — excluded from both gross and net. */
const VOID_STATUSES = new Set(["cancelled", "canceled", "void", "voided", "failed"]);
/** Card payment never completed — the order exists but no money arrived. */
const UNPAID_STATUSES = new Set(["pending", "unpaid", "awaiting_payment", "payment_failed"]);

function isRefund(status: string) {
  return REFUND_STATUSES.has(status.toLowerCase());
}
function isChargeback(status: string) {
  return CHARGEBACK_STATUSES.has(status.toLowerCase());
}
/** Reversed = money came in and then went back out. */
function isReversed(status: string) {
  return isRefund(status) || isChargeback(status);
}
function isUnpaid(status: string) {
  return UNPAID_STATUSES.has(status.toLowerCase());
}

/** Orders in these states never count toward revenue (gross or net). */
const NON_REVENUE_STATUSES = new Set([...VOID_STATUSES, ...UNPAID_STATUSES]);

/**
 * Approval creates the linked order with status "booked" — money has not
 * arrived yet, so the conversion funnel must not treat it as paid.
 */
const FUNNEL_UNPAID_STATUSES = new Set([...NON_REVENUE_STATUSES, "booked"]);

function money(cents: number) {
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

const DAY = 86_400_000;

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [views, setViews] = useState<PageView[]>([]);
  const [pending, setPending] = useState<Submission[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [signups, setSignups] = useState<Signup[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [bookingRows, setBookingRows] = useState<BookingRow[]>([]);
  const [seasonRows, setSeasonRows] = useState<SeasonRow[]>([]);
  const [range, setRange] = useState<AdminRange>(() => lastNDays(30));

  const rangeFromISO = range.from.toISOString();
  const rangeToISO = range.to.toISOString();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const since = new Date(Date.now() - 30 * DAY).toISOString();
    // Revenue sources are fetched back to whichever is older: the selected
    // range start or the 60 days the fixed 7/30-day cards compare against.
    const revenueSince = new Date(
      Math.min(new Date(rangeFromISO).getTime(), Date.now() - 60 * DAY),
    ).toISOString();

    // Seasonality needs a long history regardless of the selected range, so
    // it loads its own light slice: the last 24 months of stays.
    const seasonSince = new Date(Date.now() - 730 * DAY).toISOString().slice(0, 10);

    const [v, p, b, n, o, br, season] = await Promise.all([
      supabase
        .from("page_views")
        .select("path, device, referrer, visitor_hash, created_at")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(5000),
      supabase
        .from("gallery_submissions")
        .select("id, submitter_name, caption, created_at")
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(10),
      supabase
        .from("booking_requests")
        .select("id, name, email, arrival_date, party_size, status, created_at")
        .order("created_at", { ascending: false })
        .limit(10),
      supabase
        .from("newsletter_signups")
        .select("id, email, name, source, unsubscribed, created_at")
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("shop_orders")
        .select("id, customer_name, status, subtotal_cents, shipping_cents, total_cents, items, booking_request_id, created_at")
        .gte("created_at", revenueSince)
        .order("created_at", { ascending: false })
        .limit(2000),
      supabase
        .from("booking_requests")
        .select("id, package, source, arrival_date, departure_date, party_size, notes, status, created_at")
        .gte("created_at", revenueSince)
        .order("created_at", { ascending: false })
        .limit(2000),
      supabase
        .from("booking_requests")
        .select("id, status, arrival_date, departure_date, party_size, package, notes, created_at")
        .gte("arrival_date", seasonSince)
        .order("arrival_date", { ascending: true })
        .limit(5000),
    ]);

    const firstError = v.error ?? p.error ?? b.error ?? n.error ?? o.error ?? br.error ?? season.error;
    if (firstError) setError(firstError.message);

    setViews((v.data as PageView[]) ?? []);
    setPending((p.data as Submission[]) ?? []);
    setBookings((b.data as Booking[]) ?? []);
    setSignups((n.data as Signup[]) ?? []);
    setOrders((o.data as Order[]) ?? []);
    setBookingRows((br.data as unknown as BookingRow[]) ?? []);
    setSeasonRows((season.data as unknown as SeasonRow[]) ?? []);
    setLoading(false);
  }, [rangeFromISO]);

  useEffect(() => {
    void load();
  }, [load]);

  const metrics = useMemo(() => {
    const now = Date.now();
    const inWindow = (days: number) => views.filter((v) => now - new Date(v.created_at).getTime() <= days * DAY);
    const uniq = (rows: PageView[]) => new Set(rows.map((r) => r.visitor_hash ?? r.path + r.created_at)).size;

    const last7 = inWindow(7);
    const last30 = views;

    const byPath = new Map<string, number>();
    for (const v of last30) byPath.set(v.path, (byPath.get(v.path) ?? 0) + 1);
    const topPages = [...byPath.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);

    const byDevice = new Map<string, number>();
    for (const v of last30) byDevice.set(v.device ?? "unknown", (byDevice.get(v.device ?? "unknown") ?? 0) + 1);

    const byReferrer = new Map<string, number>();
    for (const v of last30) {
      if (!v.referrer) continue;
      let host = v.referrer;
      try {
        host = new URL(v.referrer).hostname.replace(/^www\./, "");
      } catch {
        /* keep raw */
      }
      byReferrer.set(host, (byReferrer.get(host) ?? 0) + 1);
    }
    const topReferrers = [...byReferrer.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);

    // Daily series for the last 14 days (oldest → newest).
    const days: { label: string; count: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const start = new Date(now - i * DAY);
      start.setHours(0, 0, 0, 0);
      const end = start.getTime() + DAY;
      const count = views.filter((v) => {
        const t = new Date(v.created_at).getTime();
        return t >= start.getTime() && t < end;
      }).length;
      days.push({ label: start.toLocaleDateString("en-US", { month: "numeric", day: "numeric" }), count });
    }

    return {
      views24: inWindow(1).length,
      views7: last7.length,
      views30: last30.length,
      visitors30: uniq(last30),
      visitors7: uniq(last7),
      topPages,
      topReferrers,
      byDevice: [...byDevice.entries()].sort((a, b) => b[1] - a[1]),
      days,
    };
  }, [views]);

  const finance = useMemo(() => {
    const now = Date.now();
    // Gross keeps refunded/charged-back orders (the money was captured once);
    // net subtracts them back out. Cancelled/void orders never count at all.
    const captured = orders.filter((o) => !NON_REVENUE_STATUSES.has(o.status.toLowerCase()));
    const within = (days: number, from = 0) =>
      captured.filter((o) => {
        const age = now - new Date(o.created_at).getTime();
        return age <= days * DAY && age > from * DAY;
      });
    const sum = (rows: Order[]) => rows.reduce((t, o) => t + (o.total_cents ?? 0), 0);
    const gross = sum;
    const refunds = (rows: Order[]) => sum(rows.filter((o) => isRefund(o.status)));
    const chargebacks = (rows: Order[]) => sum(rows.filter((o) => isChargeback(o.status)));
    const net = (rows: Order[]) => sum(rows.filter((o) => !isReversed(o.status)));

    const week = within(7);
    const prevWeek = within(14, 7);
    const month = within(30);
    const prevMonth = within(60, 30);
    const pct = (curr: number, prev: number) => (prev > 0 ? Math.round(((curr - prev) / prev) * 100) : null);
    const netMonth = month.filter((o) => !isReversed(o.status));

    // Net revenue by day for the last 14 days (oldest → newest).
    const days: { label: string; cents: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const start = new Date(now - i * DAY);
      start.setHours(0, 0, 0, 0);
      const end = start.getTime() + DAY;
      const cents = net(
        captured.filter((o) => {
          const t = new Date(o.created_at).getTime();
          return t >= start.getTime() && t < end;
        }),
      );
      days.push({ label: start.toLocaleDateString("en-US", { month: "numeric", day: "numeric" }), cents });
    }

    return {
      weekGrossCents: gross(week),
      weekCents: net(week),
      weekRefundCents: refunds(week),
      weekChargebackCents: chargebacks(week),
      weekReversedOrders: week.filter((o) => isReversed(o.status)).length,
      weekOrders: week.length,
      weekChange: pct(net(week), net(prevWeek)),
      monthGrossCents: gross(month),
      monthCents: net(month),
      monthRefundCents: refunds(month),
      monthChargebackCents: chargebacks(month),
      monthReversedOrders: month.filter((o) => isReversed(o.status)).length,
      monthOrders: month.length,
      monthChange: pct(net(month), net(prevMonth)),
      shippingMonth: netMonth.reduce((t, o) => t + (o.shipping_cents ?? 0), 0),
      avgOrder: netMonth.length ? Math.round(net(month) / netMonth.length) : 0,
      allTimeCents: net(captured),
      allTimeGrossCents: gross(captured),
      days,
      recent: orders.slice(0, 6),
    };
  }, [orders]);

  /**
   * Booking conversion for the selected range: how many requests arrived, how
   * many staff approved, and how many of those approvals were actually paid
   * (an approved request becomes a shop order; paid = captured, not reversed).
   */
  const funnel = useMemo(() => {
    const from = new Date(rangeFromISO).getTime();
    const to = new Date(rangeToISO).getTime();
    const requests = bookingRows.filter((r) => {
      const t = new Date(r.created_at).getTime();
      return t >= from && t <= to;
    });

    const approved = requests.filter((r) => (r.status ?? "").toLowerCase() === "approved");
    const declined = requests.filter((r) => (r.status ?? "").toLowerCase() === "declined");
    const ordersByRequest = new Map<string, Order[]>();
    for (const o of orders) {
      if (!o.booking_request_id) continue;
      const list = ordersByRequest.get(o.booking_request_id) ?? [];
      list.push(o);
      ordersByRequest.set(o.booking_request_id, list);
    }

    let paid = 0;
    let awaiting = 0;
    let paidCents = 0;
    for (const r of approved) {
      const linked = ordersByRequest.get(r.id) ?? [];
      const captured = linked.filter(
        (o) => !FUNNEL_UNPAID_STATUSES.has(o.status.toLowerCase()) && !isReversed(o.status),
      );
      if (captured.length) {
        paid += 1;
        paidCents += captured.reduce((t, o) => t + (o.total_cents ?? 0), 0);
      } else {
        awaiting += 1;
      }
    }

    const rate = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : null);

    return {
      requests: requests.length,
      approved: approved.length,
      declined: declined.length,
      pending: requests.length - approved.length - declined.length,
      paid,
      awaiting,
      paidCents,
      approvalRate: rate(approved.length, requests.length),
      paymentRate: rate(paid, approved.length),
      overallRate: rate(paid, requests.length),
    };
  }, [bookingRows, orders, rangeFromISO, rangeToISO]);

  /**
   * Monthly seasonal demand: bookings grouped by the month guests arrive, so
   * staffing can be planned against peak months rather than order dates.
   */
  const season = useMemo(() => {
    const buckets = new Map<
      string,
      { key: string; label: string; requests: number; approved: number; nights: number; guests: number }
    >();
    const now = new Date();
    for (let i = 23; i >= 0; i--) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      buckets.set(key, {
        key,
        label: d.toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" }),
        requests: 0,
        approved: 0,
        nights: 0,
        guests: 0,
      });
    }

    for (const row of seasonRows) {
      if (!row.arrival_date) continue;
      const status = (row.status ?? "").toLowerCase();
      if (status === "cancelled" || status === "canceled") continue;
      const key = row.arrival_date.slice(0, 7);
      const bucket = buckets.get(key);
      if (!bucket) continue;
      bucket.requests += 1;
      const guests = row.party_size ?? 1;
      const start = new Date(`${row.arrival_date}T00:00:00Z`).getTime();
      const end = row.departure_date ? new Date(`${row.departure_date}T00:00:00Z`).getTime() : start + DAY;
      const nights = Math.max(1, Math.round((end - start) / DAY));
      if (status === "approved") {
        bucket.approved += 1;
        bucket.nights += nights;
        bucket.guests += guests;
      }
    }

    const months = [...buckets.values()];
    const peak = months.reduce((best, m) => (m.requests > (best?.requests ?? -1) ? m : best), months[0]);
    const busiest = months.reduce((best, m) => (m.nights > (best?.nights ?? -1) ? m : best), months[0]);
    const max = Math.max(1, ...months.map((m) => m.requests));
    const totalRequests = months.reduce((sum, m) => sum + m.requests, 0);
    return { months, peak, busiest, max, totalRequests };
  }, [seasonRows]);



  /**
   * Everything scoped to the user-selected date range. Booking requests hold no
   * dollar amounts, so revenue by booking type is an ESTIMATE derived from the
   * published rate card (see lib/booking-revenue).
   */
  const breakdown = useMemo(() => {
    const from = new Date(rangeFromISO).getTime();
    const to = new Date(rangeToISO).getTime();
    const inRange = <T extends { created_at: string }>(rows: T[]) =>
      rows.filter((r) => {
        const t = new Date(r.created_at).getTime();
        return t >= from && t <= to;
      });

    const capturedOrders = inRange(orders.filter((o) => !NON_REVENUE_STATUSES.has(o.status.toLowerCase())));
    // Refunded / charged-back orders stay in gross but are excluded from net,
    // so category breakdowns (net view) only count orders the lodge kept.
    const paidOrders = capturedOrders.filter((o) => !isReversed(o.status));

    const map = new Map<string, { cents: number; units: number }>();
    for (const o of paidOrders) {
      for (const item of o.items ?? []) {
        // Booking orders carry their own category (Lodging, Boat rental, …).
        const category = (item.id ? getProduct(item.id)?.category : null) ?? item.category ?? "Other";
        const qty = item.qty ?? 1;
        const entry = map.get(category) ?? { cents: 0, units: 0 };
        entry.cents += (item.unit_price_cents ?? 0) * qty;
        entry.units += qty;
        map.set(category, entry);
      }
    }
    const shop = [...map.entries()]
      .map(([category, v]) => ({ category, ...v }))
      .sort((a, b) => b.cents - a.cents);

    const shopCents = paidOrders.reduce((t, o) => t + (o.total_cents ?? 0), 0);
    const sumTotals = (rows: Order[]) => rows.reduce((t, o) => t + (o.total_cents ?? 0), 0);
    const shopGrossCents = sumTotals(capturedOrders);
    const refundCents = sumTotals(capturedOrders.filter((o) => isRefund(o.status)));
    const chargebackCents = sumTotals(capturedOrders.filter((o) => isChargeback(o.status)));
    const bookings = totalsByType(inRange(bookingRows));
    const bookingCents = bookings.reduce((t, r) => t + r.totalCents, 0);

    return {
      bookings,
      shop,
      shopCents,
      shopGrossCents,
      refundCents,
      chargebackCents,
      reversedOrders: capturedOrders.length - paidOrders.length,
      // Explicit lists so staff can see the actual paid orders and the money
      // that went back out, not just the totals.
      paidList: [...paidOrders].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
      reversedList: capturedOrders
        .filter((o) => isReversed(o.status))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
      unpaidList: inRange(orders.filter((o) => isUnpaid(o.status))).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
      // Merch = shop-only orders (no linked booking), so lodging/boat/processing
      // line items never inflate product sales.
      merch: (() => {
        const merchOrders = paidOrders.filter((o) => !o.booking_request_id);
        const products = new Map<string, { name: string; units: number; cents: number }>();
        let units = 0;
        for (const o of merchOrders) {
          for (const item of o.items ?? []) {
            const qty = item.qty ?? 1;
            const name = (item.id ? getProduct(item.id)?.name : null) ?? item.name ?? "Item";
            const entry = products.get(name) ?? { name, units: 0, cents: 0 };
            entry.units += qty;
            entry.cents += (item.unit_price_cents ?? 0) * qty;
            products.set(name, entry);
            units += qty;
          }
        }
        const cents = merchOrders.reduce((t, o) => t + (o.total_cents ?? 0), 0);
        const shipping = merchOrders.reduce((t, o) => t + (o.shipping_cents ?? 0), 0);
        return {
          cents,
          shipping,
          units,
          orders: merchOrders.length,
          avgOrder: merchOrders.length ? Math.round(cents / merchOrders.length) : 0,
          top: [...products.values()].sort((a, b) => b.cents - a.cents).slice(0, 8),
        };
      })(),
      shopOrders: paidOrders.length,
      shopShipping: paidOrders.reduce((t, o) => t + (o.shipping_cents ?? 0), 0),
      avgOrder: paidOrders.length ? Math.round(shopCents / paidOrders.length) : 0,
      bookingCents,
      bookingRequests: bookings.reduce((t, r) => t + r.requests, 0),
      combinedCents: shopCents + bookingCents,
      days: rangeDays(range),
    };
  }, [bookingRows, orders, range, rangeFromISO, rangeToISO]);

  const maxRevenueDay = Math.max(1, ...finance.days.map((d) => d.cents));
  const maxDay = Math.max(1, ...metrics.days.map((d) => d.count));
  const activeSignups = signups.filter((s) => !s.unsubscribed);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.35em] text-[var(--color-ember)]">
            Staff Admin
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-foreground">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Visitor metrics, review queue, booking requests, and newsletter list.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-[var(--color-ember)] hover:text-[var(--color-ember)] disabled:opacity-60"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : undefined} aria-hidden />
          Refresh
        </button>
      </div>

      {error ? (
        <p role="alert" className="mt-6 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* KPI cards */}
      <section aria-label="Key metrics" className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={BarChart3} label="Page views (24h)" value={metrics.views24} hint={`${metrics.views7} in 7 days`} />
        <StatCard icon={Users} label="Unique visitors (30d)" value={metrics.visitors30} hint={`${metrics.visitors7} in 7 days`} />
        <StatCard icon={ImageIcon} label="Photos awaiting review" value={pending.length} hint="Pending queue" />
        <StatCard icon={Mail} label="Newsletter subscribers" value={activeSignups.length} hint={`${signups.length - activeSignups.length} unsubscribed`} />
      </section>

      {/* Financials */}
      <div className="mt-8">
        <PayoutAlertCard />
      </div>

      <WeekAssignmentCard />


      <section aria-label="Revenue" className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <DollarSign size={18} aria-hidden /> Revenue
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-xs text-muted-foreground">
              Cancelled orders excluded. Net = gross − refunds − chargebacks. Loaded window:{" "}
              {money(finance.allTimeCents)} net / {money(finance.allTimeGrossCents)} gross
            </p>
            <button
              type="button"
              onClick={() => exportRevenue({ finance, breakdown, range })}
              className="rounded-full border border-border px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MoneyCard
            icon={DollarSign}
            label="Net this week (7d)"
            value={money(finance.weekCents)}
            hint={`${money(finance.weekGrossCents)} gross · ${finance.weekOrders} order${finance.weekOrders === 1 ? "" : "s"}`}
            change={finance.weekChange}
          />
          <MoneyCard
            icon={TrendingUp}
            label="Net this month (30d)"
            value={money(finance.monthCents)}
            hint={`${money(finance.monthGrossCents)} gross · ${finance.monthOrders} order${finance.monthOrders === 1 ? "" : "s"}`}
            change={finance.monthChange}
          />
          <MoneyCard icon={Receipt} label="Average order (30d)" value={money(finance.avgOrder)} hint="Net, per kept order" />
          <MoneyCard icon={Receipt} label="Shipping collected (30d)" value={money(finance.shippingMonth)} hint="Included in net revenue" />
        </div>

        {/* Refunds & chargebacks */}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <ReversalCard
            title="Last 7 days"
            gross={finance.weekGrossCents}
            refunds={finance.weekRefundCents}
            chargebacks={finance.weekChargebackCents}
            net={finance.weekCents}
            count={finance.weekReversedOrders}
          />
          <ReversalCard
            title="Last 30 days"
            gross={finance.monthGrossCents}
            refunds={finance.monthRefundCents}
            chargebacks={finance.monthChargebackCents}
            net={finance.monthCents}
            count={finance.monthReversedOrders}
          />
        </div>

        <div className="mt-8">
          <h3 className="text-sm font-semibold text-foreground">Daily net revenue — last 14 days</h3>
          <div className="mt-4 flex h-32 items-end gap-2">
            {finance.days.map((d) => (
              <div key={d.label} className="flex flex-1 flex-col items-center gap-2">
                <div
                  className="w-full rounded-t bg-[var(--color-ember)]/70"
                  style={{ height: `${Math.round((d.cents / maxRevenueDay) * 100)}%`, minHeight: d.cents ? 4 : 2 }}
                  title={money(d.cents)}
                  aria-label={`${d.label}: ${money(d.cents)}`}
                />
                <span className="text-[0.6rem] text-muted-foreground">{d.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Custom-range breakdown by booking type + shop category */}
        <div className="mt-8 border-t border-border pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Revenue breakdown</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {formatRange(range)} · {breakdown.days} day{breakdown.days === 1 ? "" : "s"}
                {loading ? " · loading…" : null}
              </p>
            </div>
            <DateRangePicker value={range} onChange={setRange} />
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MoneyCard
              icon={DollarSign}
              label="Total (range)"
              value={money(breakdown.combinedCents)}
              hint="Shop + estimated bookings"
            />
            <MoneyCard
              icon={Receipt}
              label="Shop net revenue (range)"
              value={money(breakdown.shopCents)}
              hint={`${money(breakdown.shopGrossCents)} gross · ${breakdown.shopOrders} order${breakdown.shopOrders === 1 ? "" : "s"} · ${money(breakdown.avgOrder)} avg`}
            />
            <MoneyCard
              icon={CalendarDays}
              label="Bookings (range, est.)"
              value={money(breakdown.bookingCents)}
              hint={`${breakdown.bookingRequests} request${breakdown.bookingRequests === 1 ? "" : "s"}`}
            />
            <MoneyCard
              icon={TrendingUp}
              label="Shipping (range)"
              value={money(breakdown.shopShipping)}
              hint="Included in shop revenue"
            />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <BookingTypeTable rows={breakdown.bookings} rangeLabel={formatRange(range)} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Shop revenue by category
              </p>
              {breakdown.shop.length === 0 ? (
                <Empty>No shop orders in this range.</Empty>
              ) : (
                <table className="mt-3 w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="py-2 font-medium">Category</th>
                      <th className="py-2 text-right font-medium">Units</th>
                      <th className="py-2 text-right font-medium">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {breakdown.shop.map((c) => (
                      <tr key={c.category}>
                        <td className="py-2 text-foreground">{c.category}</td>
                        <td className="py-2 text-right text-muted-foreground">{c.units}</td>
                        <td className="py-2 text-right font-semibold text-foreground">{money(c.cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="mt-2 text-[0.7rem] text-muted-foreground">
                Merchandise only — excludes shipping, cancelled and refunded orders.
              </p>
            </div>
          </div>
        </div>

        {/* Paid vs refunded orders for the selected range — matches the CSV export. */}
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <OrderLedger
            title="Paid orders"
            hint={`Card payments received ${formatRange(range)}.`}
            rows={breakdown.paidList}
            empty="No paid orders in this range."
            tone="positive"
          />
          <OrderLedger
            title="Refunds & chargebacks"
            hint="Money returned to the customer or reversed by the bank."
            rows={breakdown.reversedList}
            empty="No refunds or chargebacks in this range."
            tone="negative"
          />
        </div>

        {breakdown.unpaidList.length > 0 && (
          <div className="mt-6">
            <OrderLedger
              title="Awaiting payment"
              hint="Orders placed but never paid — excluded from all revenue totals."
              rows={breakdown.unpaidList}
              empty="None."
              tone="muted"
            />
          </div>
        )}


        <div className="mt-8">
          <h3 className="text-sm font-semibold text-foreground">Recent orders</h3>
          {finance.recent.length === 0 ? (
            <Empty>No shop orders yet.</Empty>
          ) : (
            <ul className="mt-3 divide-y divide-border text-sm">
              {finance.recent.map((o) => (
                <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{o.customer_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmtDate(o.created_at)} · <span className="capitalize">{o.status}</span>
                    </p>
                  </div>
                  <span
                    className={
                      NON_REVENUE_STATUSES.has(o.status.toLowerCase()) || isReversed(o.status)
                        ? "font-semibold text-muted-foreground line-through"
                        : "font-semibold text-foreground"
                    }
                  >
                    {money(o.total_cents)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>


      {/* Merch / product sales only */}
      <section aria-label="Merch sales" className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-foreground">Merch &amp; product sales</h2>
          <p className="text-xs text-muted-foreground">
            {formatRange(range)} · shop orders only, bookings excluded
          </p>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MoneyCard
            icon={DollarSign}
            label="Merch revenue"
            value={money(breakdown.merch.cents)}
            hint="Net of refunds and chargebacks"
          />
          <MoneyCard
            icon={Receipt}
            label="Merch orders"
            value={String(breakdown.merch.orders)}
            hint={`${breakdown.merch.units} item${breakdown.merch.units === 1 ? "" : "s"} sold`}
          />
          <MoneyCard
            icon={TrendingUp}
            label="Average merch order"
            value={money(breakdown.merch.avgOrder)}
            hint="Per kept order in range"
          />
          <MoneyCard
            icon={ImageIcon}
            label="Shipping collected"
            value={money(breakdown.merch.shipping)}
            hint="Included in merch revenue"
          />
        </div>

        {breakdown.merch.top.length === 0 ? (
          <p className="mt-5 text-sm text-muted-foreground">No product sales in this range yet.</p>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[26rem] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Product</th>
                  <th className="py-2 pr-4 font-medium">Units</th>
                  <th className="py-2 font-medium">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.merch.top.map((row) => (
                  <tr key={row.name} className="border-t border-border/60">
                    <td className="py-2 pr-4 text-foreground">{row.name}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{row.units}</td>
                    <td className="py-2 text-muted-foreground">{money(row.cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Booking conversion */}
      <section aria-label="Booking conversion" className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-foreground">Booking conversion</h2>
          <p className="text-xs text-muted-foreground">Selected range · requests → approved → paid</p>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MoneyCard
            icon={Mail}
            label="Requests received"
            value={String(funnel.requests)}
            hint={`${funnel.pending} still awaiting a decision`}
          />
          <MoneyCard
            icon={Users}
            label="Approved"
            value={String(funnel.approved)}
            hint={
              funnel.approvalRate === null
                ? "No requests in range"
                : `${funnel.approvalRate}% of requests · ${funnel.declined} declined`
            }
          />
          <MoneyCard
            icon={Receipt}
            label="Paid"
            value={String(funnel.paid)}
            hint={
              funnel.paymentRate === null
                ? "No approvals in range"
                : `${funnel.paymentRate}% of approvals paid`
            }
          />
          <MoneyCard
            icon={BarChart3}
            label="Request → paid"
            value={funnel.overallRate === null ? "—" : `${funnel.overallRate}%`}
            hint={`${money(funnel.paidCents)} collected from bookings`}
          />
        </div>

        <div className="mt-5 space-y-2" aria-hidden="true">
          {[
            { label: "Requests", count: funnel.requests },
            { label: "Approved", count: funnel.approved },
            { label: "Paid", count: funnel.paid },
          ].map((step) => (
            <div key={step.label} className="flex items-center gap-3 text-sm">
              <span className="w-24 shrink-0 text-muted-foreground">{step.label}</span>
              <div className="h-3 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-[var(--color-ember)]/80"
                  style={{
                    width: funnel.requests ? `${Math.round((step.count / funnel.requests) * 100)}%` : "0%",
                    minWidth: step.count ? 6 : 0,
                  }}
                />
              </div>
              <span className="w-10 text-right font-semibold text-foreground">{step.count}</span>
            </div>
          ))}
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          Approved requests become orders automatically, so "paid" counts approvals whose order was
          captured and not refunded or charged back. {funnel.awaiting} approved{" "}
          {funnel.awaiting === 1 ? "booking is" : "bookings are"} still awaiting payment.
        </p>
      </section>


      {/* Monthly bookings — seasonal demand */}
      <section aria-label="Monthly bookings" className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-foreground">Monthly bookings</h2>
          <p className="text-xs text-muted-foreground">Last 24 months, grouped by arrival month</p>
        </div>

        {season.totalRequests === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">
            No bookings with arrival dates yet — this fills in as stays are requested.
          </p>
        ) : (
          <>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <MoneyCard
                icon={CalendarDays}
                label="Peak month"
                value={season.peak?.label ?? "—"}
                hint={`${season.peak?.requests ?? 0} requests`}
              />
              <MoneyCard
                icon={Users}
                label="Most nights booked"
                value={season.busiest?.label ?? "—"}
                hint={`${season.busiest?.nights ?? 0} guest nights approved`}
              />
              <MoneyCard
                icon={BarChart3}
                label="Total requests"
                value={String(season.totalRequests)}
                hint="Across the last 24 arrival months"
              />
            </div>

            <div className="mt-6 flex h-40 items-end gap-1 overflow-x-auto">
              {season.months.map((m) => (
                <div key={m.key} className="flex min-w-[1.75rem] flex-1 flex-col items-center gap-2">
                  <div className="flex h-full w-full items-end">
                    <div
                      className="w-full rounded-t bg-[var(--color-ember)]/80"
                      style={{
                        height: `${Math.round((m.requests / season.max) * 100)}%`,
                        minHeight: m.requests ? 4 : 2,
                      }}
                      title={`${m.label}: ${m.requests} requests, ${m.approved} approved, ${m.nights} nights`}
                      aria-label={`${m.label}: ${m.requests} requests, ${m.approved} approved, ${m.nights} guest nights`}
                    />
                  </div>
                  <span className="whitespace-nowrap text-[0.55rem] text-muted-foreground">{m.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[32rem] text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Month</th>
                    <th className="py-2 pr-4 font-medium">Requests</th>
                    <th className="py-2 pr-4 font-medium">Approved</th>
                    <th className="py-2 pr-4 font-medium">Guest nights</th>
                    <th className="py-2 font-medium">Guests</th>
                  </tr>
                </thead>
                <tbody>
                  {season.months
                    .filter((m) => m.requests > 0)
                    .map((m) => (
                      <tr key={m.key} className="border-t border-border/60">
                        <td className="py-2 pr-4 text-foreground">{m.label}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{m.requests}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{m.approved}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{m.nights}</td>
                        <td className="py-2 text-muted-foreground">{m.guests}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Booking forecast — next 6 arrival months */}
      <BookingForecastPanel rows={seasonRows} />

      {/* Traffic chart */}
      <section className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
        <h2 className="text-lg font-semibold text-foreground">Page views — last 14 days</h2>
        <div className="mt-6 flex h-40 items-end gap-2">
          {metrics.days.map((d) => (
            <div key={d.label} className="flex flex-1 flex-col items-center gap-2">
              <div
                className="w-full rounded-t bg-[var(--color-ember)]/80"
                style={{ height: `${Math.round((d.count / maxDay) * 100)}%`, minHeight: d.count ? 4 : 2 }}
                title={`${d.count} views`}
                aria-label={`${d.label}: ${d.count} views`}
              />
              <span className="text-[0.6rem] text-muted-foreground">{d.label}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          {metrics.views30.toLocaleString()} total views in the last 30 days.
        </p>
      </section>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <Panel title="Top pages (30d)">
          {metrics.topPages.length === 0 ? (
            <Empty>No traffic recorded yet.</Empty>
          ) : (
            <ul className="space-y-2 text-sm">
              {metrics.topPages.map(([path, count]) => (
                <li key={path} className="flex items-center justify-between gap-3">
                  <span className="truncate text-muted-foreground">{path}</span>
                  <span className="font-semibold text-foreground">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Top referrers (30d)">
          {metrics.topReferrers.length === 0 ? (
            <Empty>Mostly direct traffic.</Empty>
          ) : (
            <ul className="space-y-2 text-sm">
              {metrics.topReferrers.map(([host, count]) => (
                <li key={host} className="flex items-center justify-between gap-3">
                  <span className="truncate text-muted-foreground">{host}</span>
                  <span className="font-semibold text-foreground">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Devices (30d)">
          {metrics.byDevice.length === 0 ? (
            <Empty>No data yet.</Empty>
          ) : (
            <ul className="space-y-2 text-sm">
              {metrics.byDevice.map(([device, count]) => (
                <li key={device} className="flex items-center justify-between gap-3">
                  <span className="capitalize text-muted-foreground">{device}</span>
                  <span className="font-semibold text-foreground">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Photo review queue */}
      <section className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
        <div className="flex items-center justify-between gap-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <ImageIcon size={18} aria-hidden /> Photo review queue
          </h2>
          <Link to="/admin/reviews" className="text-sm font-semibold text-[var(--color-ember)] hover:underline">
            Open reviews →
          </Link>
        </div>
        {pending.length === 0 ? (
          <Empty>Nothing waiting — the queue is clear.</Empty>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {pending.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{s.submitter_name}</p>
                  <p className="truncate text-muted-foreground">{s.caption || "No caption"}</p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{fmtDate(s.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Booking requests */}
      <section className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
        <div className="flex items-center justify-between gap-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <CalendarDays size={18} aria-hidden /> Recent booking requests
          </h2>
          <Link to="/admin/inbox" className="text-sm font-semibold text-[var(--color-ember)] hover:underline">
            Open inbox →
          </Link>
        </div>
        {bookings.length === 0 ? (
          <Empty>No booking requests yet.</Empty>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {bookings.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">
                    {b.name} <span className="font-normal text-muted-foreground">· {b.email}</span>
                  </p>
                  <p className="text-muted-foreground">
                    {b.arrival_date ? `Arrives ${b.arrival_date}` : "No dates given"}
                    {b.party_size ? ` · ${b.party_size} anglers` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="rounded-full border border-border px-2.5 py-0.5 text-xs capitalize text-muted-foreground">
                    {b.status}
                  </span>
                  <span className="text-xs text-muted-foreground">{fmtDate(b.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Newsletter */}
      <section className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Mail size={18} aria-hidden /> Newsletter signups
          </h2>
          <button
            type="button"
            onClick={() => exportSignups(signups)}
            disabled={signups.length === 0}
            className="rounded-full border border-border px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:border-[var(--color-ember)] hover:text-[var(--color-ember)] disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>
        {signups.length === 0 ? (
          <Empty>No subscribers yet.</Empty>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {signups.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">{s.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.name ? `${s.name} · ` : ""}via {s.source}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {s.unsubscribed ? (
                    <span className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground">
                      Unsubscribed
                    </span>
                  ) : null}
                  <span className="text-xs text-muted-foreground">{fmtDate(s.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function csvCell(value: unknown) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function csvRow(fields: unknown[]) {
  return fields.map(csvCell).join(",");
}

/** Dollars (not cents) so the CSV sums correctly in a spreadsheet. */
function dollars(cents: number) {
  return (cents / 100).toFixed(2);
}

function downloadCsv(name: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function exportRevenue({
  finance,
  breakdown,
  range,
}: {
  finance: {
    weekCents: number;
    weekGrossCents: number;
    weekRefundCents: number;
    weekChargebackCents: number;
    weekReversedOrders: number;
    weekOrders: number;
    monthCents: number;
    monthGrossCents: number;
    monthRefundCents: number;
    monthChargebackCents: number;
    monthReversedOrders: number;
    monthOrders: number;
    avgOrder: number;
    shippingMonth: number;
    allTimeCents: number;
    allTimeGrossCents: number;
    recent: Order[];
  };
  breakdown: {
    bookings: BookingTypeTotals[];
    shop: { category: string; units: number; cents: number }[];
    merch: {
      cents: number;
      shipping: number;
      units: number;
      orders: number;
      avgOrder: number;
      top: { name: string; units: number; cents: number }[];
    };
    shopCents: number;
    shopGrossCents: number;
    refundCents: number;
    chargebackCents: number;
    reversedOrders: number;
    shopOrders: number;
    shopShipping: number;
    avgOrder: number;
    bookingCents: number;
    bookingRequests: number;
    combinedCents: number;
  };
  range: AdminRange;
}) {
  const lines: string[] = [];

  lines.push(csvRow(["Coffman Cove Lodging — revenue export"]));
  lines.push(csvRow(["Generated", new Date().toISOString()]));
  lines.push(csvRow(["Selected range", formatRange(range)]));
  lines.push("");

  lines.push(csvRow(["Section", "Metric", "Value (USD)", "Count"]));
  lines.push(csvRow(["Fixed", "Gross revenue last 7 days", dollars(finance.weekGrossCents), finance.weekOrders]));
  lines.push(csvRow(["Fixed", "Refunds last 7 days", dollars(finance.weekRefundCents), ""]));
  lines.push(csvRow(["Fixed", "Chargebacks last 7 days", dollars(finance.weekChargebackCents), finance.weekReversedOrders]));
  lines.push(csvRow(["Fixed", "Net revenue last 7 days", dollars(finance.weekCents), ""]));
  lines.push(csvRow(["Fixed", "Gross revenue last 30 days", dollars(finance.monthGrossCents), finance.monthOrders]));
  lines.push(csvRow(["Fixed", "Refunds last 30 days", dollars(finance.monthRefundCents), ""]));
  lines.push(
    csvRow(["Fixed", "Chargebacks last 30 days", dollars(finance.monthChargebackCents), finance.monthReversedOrders]),
  );
  lines.push(csvRow(["Fixed", "Net revenue last 30 days", dollars(finance.monthCents), ""]));
  lines.push(csvRow(["Fixed", "Average order (30d, net)", dollars(finance.avgOrder), ""]));
  lines.push(csvRow(["Fixed", "Shipping collected (30d)", dollars(finance.shippingMonth), ""]));
  lines.push(csvRow(["Fixed", "Loaded window net", dollars(finance.allTimeCents), ""]));
  lines.push(csvRow(["Fixed", "Loaded window gross", dollars(finance.allTimeGrossCents), ""]));
  lines.push(csvRow(["Range", "Shop gross revenue", dollars(breakdown.shopGrossCents), ""]));
  lines.push(csvRow(["Range", "Refunds", dollars(breakdown.refundCents), ""]));
  lines.push(csvRow(["Range", "Chargebacks", dollars(breakdown.chargebackCents), breakdown.reversedOrders]));
  lines.push(csvRow(["Range", "Shop net revenue", dollars(breakdown.shopCents), breakdown.shopOrders]));
  lines.push(csvRow(["Range", "Average order value", dollars(breakdown.avgOrder), ""]));
  lines.push(csvRow(["Range", "Shipping collected", dollars(breakdown.shopShipping), ""]));
  lines.push(
    csvRow(["Range", "Booking revenue (estimated)", dollars(breakdown.bookingCents), breakdown.bookingRequests]),
  );
  lines.push(csvRow(["Range", "Merch net revenue", dollars(breakdown.merch.cents), breakdown.merch.orders]));
  lines.push(csvRow(["Range", "Merch units sold", breakdown.merch.units, ""]));
  lines.push(csvRow(["Range", "Average merch order", dollars(breakdown.merch.avgOrder), ""]));
  lines.push(csvRow(["Range", "Combined total", dollars(breakdown.combinedCents), ""]));
  lines.push("");
  lines.push(csvRow(["Merch sales by product (selected range)"]));
  lines.push(csvRow(["Product", "Units", "Revenue"]));
  for (const m of breakdown.merch.top) lines.push(csvRow([m.name, m.units, dollars(m.cents)]));
  lines.push("");

  lines.push(csvRow(["Estimated booking revenue by type (selected range)"]));
  lines.push(csvRow(["Booking type", "Requests", "Lodging", "Boats", "Processing", "Total"]));
  for (const r of breakdown.bookings) {
    lines.push(
      csvRow([
        r.type,
        r.requests,
        dollars(r.lodgingCents),
        dollars(r.boatCents),
        dollars(r.processingCents),
        dollars(r.totalCents),
      ]),
    );
  }
  lines.push("");

  lines.push(csvRow(["Shop revenue by category (selected range)"]));
  lines.push(csvRow(["Category", "Units", "Revenue"]));
  for (const c of breakdown.shop) lines.push(csvRow([c.category, c.units, dollars(c.cents)]));
  lines.push("");

  lines.push(csvRow(["Recent orders"]));
  lines.push(csvRow(["Date", "Customer", "Status", "Subtotal", "Shipping", "Total"]));
  for (const o of finance.recent) {
    lines.push(
      csvRow([
        o.created_at,
        o.customer_name,
        o.status,
        dollars(o.subtotal_cents ?? 0),
        dollars(o.shipping_cents ?? 0),
        dollars(o.total_cents ?? 0),
      ]),
    );
  }

  const stamp = new Date().toISOString().slice(0, 10);
  downloadCsv(`revenue-${stamp}.csv`, lines.join("\n"));
}

function exportSignups(rows: Signup[]) {
  const csv = [
    "email,name,source,unsubscribed,created_at",
    ...rows.map((r) =>
      [r.email, r.name ?? "", r.source, String(r.unsubscribed), r.created_at]
        .map((f) => `"${String(f).replace(/"/g, '""')}"`)
        .join(","),
    ),
  ].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "newsletter-signups.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Users;
  label: string;
  value: number;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/60 p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={16} aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-[0.16em]">{label}</span>
      </div>
      <p className="mt-3 text-3xl font-bold text-foreground">{value.toLocaleString()}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function ReversalCard({
  title,
  gross,
  refunds,
  chargebacks,
  net,
  count,
}: {
  title: string;
  gross: number;
  refunds: number;
  chargebacks: number;
  net: number;
  count: number;
}) {
  const reversed = refunds + chargebacks;
  const rate = gross > 0 ? Math.round((reversed / gross) * 1000) / 10 : 0;
  return (
    <div className="rounded-xl border border-border bg-background/40 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <span className="text-xs text-muted-foreground">
          {count} reversed order{count === 1 ? "" : "s"} · {rate}%
        </span>
      </div>
      <dl className="mt-3 space-y-1.5 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-muted-foreground">Gross revenue</dt>
          <dd className="font-semibold text-foreground">{money(gross)}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-muted-foreground">Refunds</dt>
          <dd className="font-medium text-[var(--color-ember)]">−{money(refunds)}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-muted-foreground">Chargebacks</dt>
          <dd className="font-medium text-[var(--color-ember)]">−{money(chargebacks)}</dd>
        </div>
        <div className="flex items-center justify-between border-t border-border pt-1.5">
          <dt className="font-medium text-foreground">Net revenue</dt>
          <dd className="font-semibold text-foreground">{money(net)}</dd>
        </div>
      </dl>
    </div>
  );
}

function MoneyCard({
  icon: Icon,
  label,
  value,
  hint,
  change,
}: {
  icon: typeof Users;
  label: string;
  value: string;
  hint?: string;
  change?: number | null;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/60 p-5">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon size={16} aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-[0.16em]">{label}</span>
      </div>
      <p className="mt-3 text-3xl font-bold text-foreground">{value}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {hint ? <span>{hint}</span> : null}
        {typeof change === "number" ? (
          <span className={change >= 0 ? "font-semibold text-[var(--color-ember)]" : "font-semibold text-destructive"}>
            {change >= 0 ? "▲" : "▼"} {Math.abs(change)}% vs prior period
          </span>
        ) : null}
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card/60 p-6">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">{title}</h2>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="mt-4 text-sm text-muted-foreground">{children}</p>;
}

function BookingTypeTable({ rows, rangeLabel }: { rows: BookingTypeTotals[]; rangeLabel: string }) {
  const total = rows.reduce((t, r) => t + r.totalCents, 0);
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Estimated booking revenue by type
      </p>
      <p className="sr-only">{rangeLabel}</p>
      {rows.length === 0 ? (
        <Empty>No booking requests in this range.</Empty>
      ) : (
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="py-2 font-medium">Booking type</th>
              <th className="py-2 text-right font-medium">Reqs</th>
              <th className="py-2 text-right font-medium">Lodging</th>
              <th className="py-2 text-right font-medium">Boats</th>
              <th className="py-2 text-right font-medium">Proc.</th>
              <th className="py-2 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.type}>
                <td className="py-2 text-foreground">{r.type}</td>
                <td className="py-2 text-right text-muted-foreground">{r.requests}</td>
                <td className="py-2 text-right text-muted-foreground">{money(r.lodgingCents)}</td>
                <td className="py-2 text-right text-muted-foreground">{money(r.boatCents)}</td>
                <td className="py-2 text-right text-muted-foreground">{money(r.processingCents)}</td>
                <td className="py-2 text-right font-semibold text-foreground">{money(r.totalCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border">
              <td className="py-2 text-xs uppercase tracking-wider text-muted-foreground" colSpan={5}>
                Estimated total
              </td>
              <td className="py-2 text-right font-semibold text-foreground">{money(total)}</td>
            </tr>
          </tfoot>
        </table>
      )}
      <p className="mt-2 text-[0.7rem] text-muted-foreground">
        Estimated from published rates ($200/person/night lodging, $400/day boat, ~$150/box
        processing) — booking requests are inquiries and store no amounts.
      </p>
    </div>
  );
}

function OrderLedger({
  title,
  hint,
  rows,
  empty,
  tone,
}: {
  title: string;
  hint: string;
  rows: Order[];
  empty: string;
  tone: "positive" | "negative" | "muted";
}) {
  const total = rows.reduce((t, o) => t + (o.total_cents ?? 0), 0);
  const amountClass =
    tone === "negative"
      ? "font-semibold text-[var(--color-ember)]"
      : tone === "muted"
        ? "font-semibold text-muted-foreground"
        : "font-semibold text-foreground";

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-sm">
          <span className="text-muted-foreground">{rows.length} order{rows.length === 1 ? "" : "s"} · </span>
          <span className={amountClass}>
            {tone === "negative" && total > 0 ? "-" : ""}
            {money(total)}
          </span>
        </p>
      </div>
      {rows.length === 0 ? (
        <Empty>{empty}</Empty>
      ) : (
        <ul className="mt-3 max-h-72 divide-y divide-border overflow-y-auto text-sm">
          {rows.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground">{o.customer_name}</p>
                <p className="text-xs text-muted-foreground">
                  #{o.id.slice(0, 8).toUpperCase()} · {fmtDate(o.created_at)} ·{" "}
                  <span className="capitalize">{o.status.replace(/_/g, " ")}</span>
                </p>
              </div>
              <span className={amountClass}>
                {tone === "negative" ? "-" : ""}
                {money(o.total_cents)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[0.7rem] text-muted-foreground">{hint}</p>
    </div>
  );
}

export default AdminDashboard;
